/**
 * Plan tier context limits for Project Revenant.
 *
 * IMPORTANT: Only the number of registered Tracked Places (contexts) is limited
 * by plan tier. Test executions, organization user seats, and scans per context
 * are NOT metered. There is no cap on how many times a registered context can
 * be tested, nor on how many users belong to an organization.
 */

export const PLAN_LIMITS: Record<string, number | null> = {
  free: 3,
  starter: 10,
  professional: 50,
  enterprise: null, // null = unlimited
};

export type UsageSeverity = 'ok' | 'near' | 'at';

export interface UsageThreshold {
  color: 'success' | 'warning' | 'error' | 'primary';
  severity: UsageSeverity;
  /** Clamped to [0, 100] */
  percentage: number;
}

/**
 * Compute a display threshold from raw used/limit values.
 *
 * Thresholds:
 *   - under 80%  → success (green)
 *   - 80–99%     → warning (yellow)
 *   - 100%+      → error (red)
 *   - null limit → primary (unlimited, no bar needed)
 */
export function getUsageThreshold(used: number, limit: number | null): UsageThreshold {
  if (limit === null) {
    return { color: 'primary', severity: 'ok', percentage: 0 };
  }

  const raw = limit > 0 ? (used / limit) * 100 : 100;
  const percentage = Math.min(raw, 100);

  if (used >= limit) {
    return { color: 'error', severity: 'at', percentage: 100 };
  }
  if (percentage >= 80) {
    return { color: 'warning', severity: 'near', percentage };
  }
  return { color: 'success', severity: 'ok', percentage };
}

export function getPlanDisplayName(plan: string): string {
  const names: Record<string, string> = {
    free: 'Free',
    starter: 'Starter',
    professional: 'Professional',
    enterprise: 'Enterprise',
  };
  return names[plan] ?? plan;
}
